import { useCallback, useMemo } from 'react';
import { useConfig } from '../ConfigProvider';
import { useTunnelData } from '../TunnelContext';
import { pushToDataLayer, clearEcommerceData } from './gtmDataLayer';
import { FUNNEL_EVENTS } from './eventDefinitions';
import {
  formatOfferForEcommerce,
  formatOptionsForEcommerce,
  formatCartForEcommerce,
  getTotalValue
} from './ecommerceHelpers';

export function useAnalytics() {
  const config = useConfig();
  const { tunnel } = useTunnelData();

  // Check if analytics is enabled for this brand
  const isEnabled = useMemo(() => {
    return !!(config.analytics?.gtm || config.analytics?.gtag);
  }, [config.analytics]);

  // Get event name, allowing per-brand overrides
  const getEventName = useCallback((eventKey) => {
    const customEvents = config.analytics?.events || {};
    return customEvents[eventKey] || FUNNEL_EVENTS[eventKey] || eventKey;
  }, [config.analytics]);

  // Check if specific event is enabled for this brand
  const isEventEnabled = useCallback((eventKey) => {
    if (!isEnabled) return false;

    const disabledEvents = config.analytics?.disabledEvents || [];
    return !disabledEvents.includes(eventKey);
  }, [isEnabled, config.analytics]);

  // Get common data to include with every event
  const getCommonData = useCallback(() => ({
    brand_code: config.code,
    brand_name: config.companyName,
    club_id: tunnel.club?.msdsId,
    club_name: tunnel.club?.name,
    tunnel_uid: tunnel.uid,
    tracker_uid: tunnel.trackerUid,
    user_email: tunnel.user?.email || null,
    utm_source: tunnel.utm_source,
    utm_medium: tunnel.utm_medium,
    utm_campaign: tunnel.utm_campaign
  }), [config, tunnel]);

  // Track tunnel start (club selected)
  const trackTunnelStart = useCallback((club) => {
    if (!isEventEnabled('TUNNEL_START')) return;

    pushToDataLayer(getEventName('TUNNEL_START'), {
      ...getCommonData(),
      club_id: club.msdsId,
      club_name: club.name,
      club_city: club.city,
      club_zip: club.zip
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track user form started
  const trackUserFormStarted = useCallback(() => {
    if (!isEventEnabled('USER_FORM_STARTED')) return;

    pushToDataLayer(getEventName('USER_FORM_STARTED'), {
      ...getCommonData()
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track user form completed
  const trackUserFormCompleted = useCallback(() => {
    if (!isEventEnabled('USER_FORM_COMPLETED')) return;

    pushToDataLayer(getEventName('USER_FORM_COMPLETED'), {
      ...getCommonData(),
      user_has_email: !!tunnel.user?.email,
      user_has_phone: !!tunnel.user?.phone
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel.user]);

  // Track offer selected (with ecommerce data)
  const trackOfferSelected = useCallback((offer) => {
    if (!isEventEnabled('OFFER_SELECTED')) return;

    clearEcommerceData();

    const item = formatOfferForEcommerce(offer, tunnel.club?.name);

    pushToDataLayer(getEventName('OFFER_SELECTED'), {
      ...getCommonData(),
      offer_id: offer['@id'],
      offer_name: offer.name,
      offer_type: offer.type,
      offer_price: offer.offer?.priceTI ? (offer.offer.priceTI / 100) : 0,
      ecommerce: {
        currency: 'EUR',
        items: item ? [item] : []
      }
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel.club]);

  // Track options selected (with ecommerce data)
  const trackOptionsSelected = useCallback((options) => {
    if (!isEventEnabled('OPTIONS_SELECTED')) return;

    clearEcommerceData();

    const items = formatOptionsForEcommerce(options, tunnel.club?.name);

    pushToDataLayer(getEventName('OPTIONS_SELECTED'), {
      ...getCommonData(),
      options_count: options.length,
      options_names: options.map(o => o.name).join(', '),
      ecommerce: {
        currency: 'EUR',
        items: items
      }
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel.club]);

  // Track promo code applied
  const trackPromoCodeApplied = useCallback((code, discount = null) => {
    if (!isEventEnabled('PROMO_CODE_APPLIED')) return;

    pushToDataLayer(getEventName('PROMO_CODE_APPLIED'), {
      ...getCommonData(),
      promo_code: code,
      discount_amount: discount
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track promo code error
  const trackPromoCodeError = useCallback((code, errorMessage) => {
    if (!isEventEnabled('PROMO_CODE_ERROR')) return;

    pushToDataLayer(getEventName('PROMO_CODE_ERROR'), {
      ...getCommonData(),
      promo_code: code,
      error_message: errorMessage
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track SEPA submitted
  const trackSepaSubmitted = useCallback(() => {
    if (!isEventEnabled('SEPA_SUBMITTED')) return;

    pushToDataLayer(getEventName('SEPA_SUBMITTED'), {
      ...getCommonData()
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track contract viewed
  const trackContractViewed = useCallback(() => {
    if (!isEventEnabled('CONTRACT_VIEWED')) return;

    pushToDataLayer(getEventName('CONTRACT_VIEWED'), {
      ...getCommonData()
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track contract signed
  const trackContractSigned = useCallback(() => {
    if (!isEventEnabled('CONTRACT_SIGNED')) return;

    pushToDataLayer(getEventName('CONTRACT_SIGNED'), {
      ...getCommonData(),
      contract_signed_at: new Date().toISOString()
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track payment started
  const trackPaymentStarted = useCallback(() => {
    if (!isEventEnabled('PAYMENT_STARTED')) return;

    clearEcommerceData();

    const items = formatCartForEcommerce(tunnel);

    pushToDataLayer(getEventName('PAYMENT_STARTED'), {
      ...getCommonData(),
      ecommerce: {
        currency: 'EUR',
        items: items
      }
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel]);

  // Track payment completed (with full ecommerce purchase data)
  const trackPaymentCompleted = useCallback((transactionId = null) => {
    if (!isEventEnabled('PAYMENT_COMPLETED')) return;

    clearEcommerceData();

    const items = formatCartForEcommerce(tunnel);
    const totalValue = getTotalValue(tunnel);

    pushToDataLayer(getEventName('PAYMENT_COMPLETED'), {
      ...getCommonData(),
      transaction_id: transactionId || tunnel.sales?.['@id'],
      ecommerce: {
        transaction_id: transactionId || tunnel.sales?.['@id'],
        value: totalValue,
        currency: 'EUR',
        items: items
      }
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel]);

  // Track payment error
  const trackPaymentError = useCallback((errorMessage) => {
    if (!isEventEnabled('PAYMENT_ERROR')) return;

    pushToDataLayer(getEventName('PAYMENT_ERROR'), {
      ...getCommonData(),
      error_message: errorMessage
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track club transfer completed
  const trackClubTransferCompleted = useCallback(() => {
    if (!isEventEnabled('CLUB_TRANSFER_COMPLETED')) return;

    pushToDataLayer(getEventName('CLUB_TRANSFER_COMPLETED'), {
      ...getCommonData()
    });
  }, [isEventEnabled, getEventName, getCommonData]);

  // Track tunnel completed (confirmation page)
  const trackTunnelCompleted = useCallback(() => {
    if (!isEventEnabled('TUNNEL_COMPLETED')) return;

    clearEcommerceData();

    const items = formatCartForEcommerce(tunnel);
    const totalValue = getTotalValue(tunnel);

    pushToDataLayer(getEventName('TUNNEL_COMPLETED'), {
      ...getCommonData(),
      conversion_value: totalValue,
      ecommerce: {
        transaction_id: tunnel.sales?.['@id'],
        value: totalValue,
        currency: 'EUR',
        items: items
      }
    });
  }, [isEventEnabled, getEventName, getCommonData, tunnel]);

  return {
    isEnabled,
    trackTunnelStart,
    trackUserFormStarted,
    trackUserFormCompleted,
    trackOfferSelected,
    trackOptionsSelected,
    trackPromoCodeApplied,
    trackPromoCodeError,
    trackSepaSubmitted,
    trackContractViewed,
    trackContractSigned,
    trackPaymentStarted,
    trackPaymentCompleted,
    trackPaymentError,
    trackClubTransferCompleted,
    trackTunnelCompleted,
    // Low-level access for custom events
    pushToDataLayer: isEnabled ? pushToDataLayer : () => {},
    getCommonData
  };
}
